import { useCallback, useEffect, useState } from 'react';
import { useSession, useSupabaseClient } from '@supabase/auth-helpers-react';
import { toast } from 'sonner';
import type { Database } from '@/integrations/supabase/types';
import { createInitialBoard, getPlayerNumberForIdentity, hasPlayerSlot, hasTwoPlayers } from '@/lib/game';
import { ensureGuestName, getStoredGuestName } from '@/lib/player-identity';

type Game = Database['public']['Tables']['games']['Row'];
type GameUpdate = Database['public']['Tables']['games']['Update'];

const ACTIVE_ROOM_TTL_MS = 5 * 60 * 1000;

export function useRooms() {
  const session = useSession();
  const supabase = useSupabaseClient<Database>();
  const [rooms, setRooms] = useState<Game[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchRooms = useCallback(async () => {
    try {
      setLoading(true);
      console.log('Buscando salas...');

      const { data, error } = await supabase
        .from('games')
        .select('*')
        .is('winner_id', null)
        .gt('updated_at', new Date(Date.now() - ACTIVE_ROOM_TTL_MS).toISOString())
        .order('created_at', { ascending: false });

      if (error) throw error;

      const validRooms = data?.filter((room) => (
        room.status !== 'finished' &&
        !hasTwoPlayers(room) &&
        (
          hasPlayerSlot(room.player1_id, room.player1_name) ||
          hasPlayerSlot(room.player2_id, room.player2_name)
        )
      )) || [];

      console.log('Salas válidas encontradas:', validRooms);
      setRooms(validRooms);
    } catch (err) {
      console.error('Erro ao carregar salas:', err);
      toast.error('Erro ao carregar salas');
    } finally {
      setLoading(false);
    }
  }, [supabase]);

  const deleteEmptyRoom = useCallback(async (roomId: string) => {
    try {
      console.log('Removendo sala vazia:', roomId);
      const { error } = await supabase
        .from('games')
        .delete()
        .match({ room_id: roomId });

      if (error) throw error;
      console.log('Sala removida com sucesso:', roomId);
      await fetchRooms();
    } catch (err) {
      console.error('Erro ao remover sala:', err);
    }
  }, [fetchRooms, supabase]);

  useEffect(() => {
    fetchRooms();

    const roomsSubscription = supabase
      .channel('public:games')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'games',
      }, (payload) => {
        console.log('Mudança detectada:', payload);
        fetchRooms();
      })
      .subscribe();

    const refreshInterval = setInterval(fetchRooms, 30000);

    return () => {
      roomsSubscription.unsubscribe();
      clearInterval(refreshInterval);
    };
  }, [fetchRooms, supabase]);

  const sendSystemMessage = useCallback(async (gameId: string, message: string, eventType: string) => {
    try {
      const { error } = await supabase
        .from('chat_messages')
        .insert({
          game_id: gameId,
          message,
          is_system_message: true,
          system_event_type: eventType,
          created_at: new Date().toISOString(),
        });

      if (error) throw error;
    } catch (err) {
      console.error('Erro ao enviar mensagem do sistema:', err);
    }
  }, [supabase]);

  const leaveRoom = useCallback(async (roomId: string) => {
    try {
      const { data: game } = await supabase
        .from('games')
        .select()
        .eq('room_id', roomId)
        .single();

      if (!game) return;

      let messageName = 'Anônimo';
      const guestName = session?.user ? null : getStoredGuestName();

      if (session?.user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('display_name')
          .eq('id', session.user.id)
          .single();

        messageName = profile?.display_name || session.user.email || messageName;
      } else {
        messageName = guestName || messageName;
      }

      const playerNumber = getPlayerNumberForIdentity(game, {
        userId: session?.user?.id,
        guestName,
      });

      const isFinishedGame = game.status === 'finished' || game.winner_id !== null;
      const updates: GameUpdate = {
        updated_at: new Date().toISOString(),
      };

      if (!isFinishedGame) {
        updates.status = 'waiting';
      }

      if (playerNumber === 1) {
        messageName = game.player1_name || messageName;
        updates.player1_id = null;
        updates.player1_name = null;
      } else if (playerNumber === 2) {
        messageName = game.player2_name || messageName;
        updates.player2_id = null;
        updates.player2_name = null;
      } else {
        return;
      }

      const { error } = await supabase
        .from('games')
        .update(updates)
        .eq('room_id', roomId);

      if (error) throw error;

      await sendSystemMessage(game.id, `👋 ${messageName} saiu`, 'player_left');

      const { data: updatedGame } = await supabase
        .from('games')
        .select()
        .eq('room_id', roomId)
        .single();

      if (
        updatedGame &&
        updatedGame.status !== 'finished' &&
        updatedGame.winner_id === null &&
        !hasPlayerSlot(updatedGame.player1_id, updatedGame.player1_name) &&
        !hasPlayerSlot(updatedGame.player2_id, updatedGame.player2_name)
      ) {
        await deleteEmptyRoom(roomId);
      }

      toast.success('Você saiu da sala');
      await fetchRooms();
    } catch (err) {
      console.error('Erro ao sair da sala:', err);
      toast.error('Erro ao sair da sala');
    }
  }, [deleteEmptyRoom, fetchRooms, sendSystemMessage, session?.user, supabase]);

  const createRoom = useCallback(async () => {
    try {
      console.log('Criando sala...');
      const roomId = Math.random().toString(36).substring(2, 8).toUpperCase();
      const initialBoard = createInitialBoard();

      let playerName = session?.user ? 'Anônimo' : ensureGuestName();
      let playerId: string | null = null;

      if (session?.user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('display_name')
          .eq('id', session.user.id)
          .single();

        playerName = profile?.display_name || session.user.email || 'Anônimo';
        playerId = session.user.id;
      }

      const { data, error } = await supabase
        .from('games')
        .insert({
          room_id: roomId,
          board: initialBoard,
          current_player: 1,
          player1_id: playerId,
          player1_name: playerName,
          status: 'waiting',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .select()
        .single();

      if (error) {
        console.error('Erro ao criar sala:', error);
        throw error;
      }

      await sendSystemMessage(data.id, `👋 ${playerName} entrou`, 'player_joined');

      console.log('Sala criada com sucesso:', data);
      toast.success('Sala criada com sucesso!');
      await fetchRooms();
      return data;
    } catch (err) {
      console.error('Erro ao criar sala:', err);
      toast.error('Erro ao criar sala');
      return null;
    }
  }, [fetchRooms, sendSystemMessage, session?.user, supabase]);

  const cleanupRooms = useCallback(async () => {
    if (!session?.user) return;

    try {
      console.log('Iniciando limpeza de salas...');

      const inactiveSince = new Date(Date.now() - ACTIVE_ROOM_TTL_MS).toISOString();
      const { data, error } = await supabase
        .from('games')
        .select('*')
        .or('player1_id.is.null,player2_id.is.null')
        .or(`updated_at.lt.${inactiveSince}`);

      if (error) throw error;

      const roomsToDelete = data?.filter((room) => {
        const hasAnyPlayer =
          hasPlayerSlot(room.player1_id, room.player1_name) ||
          hasPlayerSlot(room.player2_id, room.player2_name);
        const isInactive = new Date(room.updated_at).getTime() < Date.now() - ACTIVE_ROOM_TTL_MS;

        return !hasAnyPlayer || isInactive;
      }).map((room) => room.room_id) || [];

      if (roomsToDelete.length > 0) {
        const { error: deleteError } = await supabase
          .from('games')
          .delete()
          .in('room_id', roomsToDelete);

        if (!deleteError) {
          console.log(`Salas removidas: ${roomsToDelete.join(', ')}`);
          toast.success(`${roomsToDelete.length} salas antigas foram removidas`);
        }
      } else {
        console.log('Nenhuma sala para limpar');
        toast.info('Nenhuma sala para limpar');
      }

      await fetchRooms();
    } catch (err) {
      console.error('Erro ao limpar salas:', err);
      toast.error('Erro ao limpar salas');
    }
  }, [fetchRooms, session?.user, supabase]);

  return {
    rooms,
    loading,
    createRoom,
    leaveRoom,
    cleanupRooms,
    refetch: fetchRooms,
  };
}
